import {FC, useCallback} from 'react';
import {LocalAudioTrack} from 'livekit-client';
import {useMaybeRoomContext, useMediaDeviceSelect} from '@livekit/components-react';
import {BlinkingButton, IQuickMenuItem, QuickMenu} from 'ui-kit';
import {Box} from '@chakra-ui/react';
import {useT} from '@kcast-fe/core';

interface IProps {
  track: LocalAudioTrack | undefined;
}

const ID_SEPARATOR = '::';
const TOOLTIP_ID = 'VOICE_MODE_SETTINGS';

export const SettingsButton: FC<IProps> = ({track}) => {
  const room = useMaybeRoomContext();
  const {t} = useT();

  const handleError = useCallback((e: Error) => {
    console.error(`[Device] Error:`, e.message);
  }, []);

  const {
    devices: inputDevices,
    activeDeviceId: activeInputId,
    setActiveMediaDevice: setActiveInput
  } = useMediaDeviceSelect({
    room,
    track,
    kind: 'audioinput',
    requestPermissions: true,
    onError: handleError
  });

  const {
    devices: outputDevices,
    activeDeviceId: activeOutputId,
    setActiveMediaDevice: setActiveOutput
  } = useMediaDeviceSelect({
    room,
    track,
    kind: 'audiooutput',
    onError: handleError
  });

  const buildMenuItemId = (deviceId: string, kind: MediaDeviceKind) => {
    return `${deviceId}${ID_SEPARATOR}${kind}`;
  };

  // Input devices list. Id can be "default"
  const inputItems: Array<IQuickMenuItem<string>> = inputDevices.map((i) => ({
    name: i.label,
    _internalId: i.deviceId,
    id: buildMenuItemId(i.deviceId, 'audioinput'),
    category: t('voice.microphone'),
    onClick: () => setActiveInput(i.deviceId)
  }));

  // Output devices list. Id can be "default"
  const outputItems: Array<IQuickMenuItem<string>> = outputDevices.map((i) => ({
    name: i.label,
    _internalId: i.deviceId,
    id: buildMenuItemId(i.deviceId, 'audiooutput'),
    category: t('voice.speaker'),
    onClick: () => setActiveOutput(i.deviceId)
  }));

  return (
    <Box>
      <QuickMenu
        items={[...inputItems, ...outputItems]}
        activeId={[
          buildMenuItemId(activeInputId, 'audioinput'),
          buildMenuItemId(activeOutputId, 'audiooutput')
        ]}
        trigger={
          <BlinkingButton
            icon={'Settings'}
            tooltipId={TOOLTIP_ID}
            tooltipLabel={t('voice.settings')}
          />
        }
      />
    </Box>
  );
};
